import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { CarpoolReport, ReportStatus } from '../../../../model/Report';
import { fetchReports, ignoreReport, applyReport } from '../../../../services/api';
import Modal from '../../../../components/Modal';
import PageHeader from '../../../../components/PageHeader';
import { useLanguage, useMessages } from '../../../../i18n/I18n';
import './ReportingEditor.css';
import LoadingPlaceholder from '../../../../components/LoadingPlaceholder';
import { loadTabData } from '../../../../services/tabData';

interface ReportingEditorProps {
  sessionToken: string;
  festivalId: string;
}

interface ActionModalProps {
  report: CarpoolReport;
  action: 'ignore' | 'delete';
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

const ActionModal: React.FC<ActionModalProps> = ({ report, action, onConfirm, onCancel }) => {
  const t = useMessages('reports')[action];
  const common = useMessages('common');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await onConfirm();
      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failed);
      setIsProcessing(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={() => !isProcessing && onCancel()} className="reports-dialog">
      <div className="modal-header">
        <h3>{t.title}</h3>
        {!isProcessing && (
          <button type="button" className="btn btn-ghost btn-icon dialog-close" onClick={onCancel} aria-label={common.close}>
            <FiX aria-hidden="true" />
          </button>
        )}
      </div>
      {error && <div className="modal-error">{error}</div>}
      <p>{t.confirm(report.carpool.user_name)}</p>
      <div className="modal-actions">
        <button
          className={action === 'delete' ? 'danger' : undefined}
          onClick={handleConfirm}
          disabled={isProcessing}
        >
          {isProcessing ? t.busy : t.button}
        </button>
        <button
          onClick={onCancel}
          disabled={isProcessing}
        >
          {common.cancel}
        </button>
      </div>
    </Modal>
  );
};

const ReportingEditor: React.FC<ReportingEditorProps> = ({ sessionToken, festivalId }) => {
  const t = useMessages('reports');
  const { locale } = useLanguage();
  const [reports, setReports] = useState<CarpoolReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<{ report: CarpoolReport; action: 'ignore' | 'delete' } | null>(null);

  const loadReports = async () => {
    try {
      setIsLoading(true);
      const data = await loadTabData(festivalId, 'reports', () => fetchReports(sessionToken, festivalId));
      setReports(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.loadFailed);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [sessionToken, festivalId]);

  const handleIgnore = async (reportId: string) => {
    await ignoreReport(sessionToken, festivalId, reportId);
    await loadReports();
  };

  const handleDelete = async (reportId: string) => {
    await applyReport(sessionToken, festivalId, reportId);
    await loadReports();
  };

  if (isLoading) {
    return (
      <div className="reporting-editor">
        <PageHeader title={t.title} description={t.description} />
        <LoadingPlaceholder layout="table" count={3} />
      </div>
    );
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(locale);
  };

  const getStatusClass = (status: ReportStatus) => {
    return `report-status ${status}`;
  };

  return (
    <div className="reporting-editor">
      <PageHeader
        title={t.title}
        count={reports.length}
        description={t.description}
      />
      {reports.length === 0 ? (
        <div className="empty-state">{t.empty}</div>
      ) : (
        <div className="data-table">
          <div className="data-table-scroll">
            <div className="reports-table" role="table" aria-label={t.title}>
              <div className="data-table-head" role="row">
                <span role="columnheader">{t.created}</span>
                <span role="columnheader">{t.status}</span>
                <span role="columnheader">{t.reportedBy}</span>
                <span role="columnheader">{t.carpool}</span>
                <span role="columnheader">{t.offeredBy}</span>
                <span role="columnheader" className="reports-actions-head">{t.actions}</span>
              </div>
              {reports.map(reportItem => (
                <div className="data-table-row" role="row" key={reportItem.report.identifier}>
                  <span role="cell" className="report-date">{formatDate(reportItem.report.created_at)}</span>
                  <span role="cell">
                    <span className={getStatusClass(reportItem.report.status)}>
                      {t.statusLabel(reportItem.report.status)}
                    </span>
                  </span>
                  <span role="cell" className="report-reporter">{reportItem.report.reported_by_device_id}</span>
                  <span role="cell" className="report-details">
                    <span className="report-line"><span className="report-label">{t.direction}</span> {t.directionValue(reportItem.carpool.direction)}</span>
                    <span className="report-line"><span className="report-label">{t.departure}</span> {formatDate(reportItem.carpool.departure_date)}</span>
                  </span>
                  <span role="cell" className="report-details">
                    <span className="report-line"><span className="report-label">{t.name}</span> {reportItem.carpool.user_name}</span>
                    <span className="report-line"><span className="report-label">{t.phone}</span> {reportItem.carpool.user_phone}</span>
                  </span>
                  <span role="cell" className="report-actions">
                    {reportItem.report.status === 'pending' && (
                      <>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => setSelectedReport({ report: reportItem, action: 'ignore' })}
                        >
                          {t.ignore.button}
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => setSelectedReport({ report: reportItem, action: 'delete' })}
                        >
                          {t.delete.button}
                        </button>
                      </>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {selectedReport && (
        <ActionModal
          report={selectedReport.report}
          action={selectedReport.action}
          onConfirm={() =>
            selectedReport.action === 'ignore'
              ? handleIgnore(selectedReport.report.report.identifier)
              : handleDelete(selectedReport.report.report.identifier)
          }
          onCancel={() => setSelectedReport(null)}
        />
      )}
    </div>
  );
};

export default ReportingEditor;
